import { useId, useState, type ReactNode } from "react";
import { Box, Button, Chip } from "@mui/joy";
import { CaretDown, FunnelSimple } from "@phosphor-icons/react";

/** Keeps controls mounted so closing a panel never clears searches or saved views. */
export function CollapsibleFilters({
  children,
  activeCount = 0,
  collapsible = true,
  label = "Search, filters & sort",
}: {
  children: ReactNode;
  activeCount?: number;
  collapsible?: boolean;
  label?: string;
}) {
  const [open, setOpen] = useState(false);
  const id = useId();
  if (!collapsible) return <>{children}</>;
  return (
    <Box
      sx={{
        width: "100%",
        minWidth: 0,
        mb: 2,
        border: "1px solid",
        borderColor: "divider",
        borderRadius: "md",
        bgcolor: "background.surface",
      }}
    >
      <Button
        id={`${id}-toggle`}
        variant="plain"
        color="neutral"
        aria-expanded={open}
        aria-controls={id}
        onClick={() => setOpen((value) => !value)}
        startDecorator={<FunnelSimple size={18} />}
        endDecorator={
          <>
            <Chip
              size="sm"
              variant="soft"
              sx={{ display: activeCount ? undefined : "none" }}
            >
              {activeCount} active
            </Chip>
            <CaretDown
              size={16}
              style={{ transform: open ? "rotate(180deg)" : undefined }}
            />
          </>
        }
        sx={{
          width: "100%",
          justifyContent: "flex-start",
          px: 2,
          py: 1.25,
          "& .MuiButton-endDecorator": { ml: "auto" },
        }}
      >
        {label}
      </Button>
      <Box
        id={id}
        role="region"
        aria-labelledby={`${id}-toggle`}
        hidden={!open}
        sx={{
          display: open ? "block" : "none",
          p: 1.5,
          borderTop: "1px solid",
          borderColor: "divider",
          "& > .MuiCard-root": { border: 0, boxShadow: "none", m: 0 },
          "& > .MuiStack-root, & > .MuiBox-root": { mb: 0 },
        }}
      >
        {children}
      </Box>
    </Box>
  );
}
